import { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

const DB_NAME = 'v86';
const STORE = 'state';
const KEY = 'linux';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function dbGet(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function dbPut(db, val) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(val, KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function vmSaveState(emu) {
  return new Promise((resolve, reject) => {
    emu.save_state((err, state) => err ? reject(err) : resolve(state));
  });
}

// Bump this when you change SETUP_CMDS, add files, or modify initial state
const STATE_VERSION = 2;

const PS1_CMD =
  "export PS1=\"$(printf '\\033[1;32m')user$(printf '\\033[0m')@" +
  "$(printf '\\033[1;34m')diaddigo.com$(printf '\\033[0m'):" +
  "$(printf '\\033[1;33m')\\\\w$(printf '\\033[0m')\\\\$ \"";

const SETUP_CMDS =
  PS1_CMD + '\n' +
  "cat > ~/about.txt << 'EOF'\njosh diaddigo\nhttps://github.com/joshdiaddigo\nhttps://linkedin.com/in/joshdiaddigo\nEOF\n" +
  "clear\n";

export default function App() {
  const termRef = useRef(null);
  const instRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (instRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      convertEol: false,
      theme: {
        background: '#000000',
        foreground: '#cccccc',
        cursor: '#ffffff',
        selectionBackground: '#333333',
      },
      fontSize: 15,
      fontFamily: 'monospace',
      scrollback: 5000,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(termRef.current);
    fitAddon.fit();
    instRef.current = { term, fitAddon };

    if (typeof window.V86 === 'undefined') {
      term.write('\x1b[31mv86 failed to load. Try Chrome or Firefox.\x1b[0m\r\n');
      return;
    }

    let emulator, db;
    let outputBuffer = '';
    let flushTimeout = null;
    let configured = false;
    let bootBuffer = '';

    function flush(char) {
      outputBuffer += char;
      if (!flushTimeout) {
        flushTimeout = setTimeout(() => {
          term.write(outputBuffer);
          outputBuffer = '';
          flushTimeout = null;
        }, 10);
      }
    }

    async function start() {
      db = await openDB();
      const entry = await dbGet(db);
      const saved = entry && entry.version === STATE_VERSION ? entry.state : null;

      const config = {
        wasm_path: '/v86/v86.wasm',
        memory_size: 128 * 1024 * 1024,
        vga_memory_size: 2 * 1024 * 1024,
        bios: { url: '/v86/seabios.bin' },
        vga_bios: { url: '/v86/vgabios.bin' },
        cdrom: { url: '/v86/linux4.iso' },
        filesystem: {},
        autostart: true,
        disable_keyboard: true,
        disable_mouse: true,
        disable_speaker: true,
      };

      if (saved) {
        config.initial_state = { buffer: saved };
        configured = true;
      }

      emulator = new window.V86(config);
      instRef.current.emulator = emulator;

      if (saved) {
        emulator.add_listener('emulator-started', () => {
          emulator.serial0_send('\n');
        });
      }

      emulator.add_listener('serial0-output-byte', function(byte) {
        if (byte === 0xFF) return;
        if (loading) setLoading(false);
        const char = String.fromCharCode(byte);

        // Always show output - never suppress
        flush(char);

        // On first boot, detect prompt and send setup commands
        if (!configured) {
          bootBuffer += char;
          const tail = bootBuffer.slice(-2);
          if (tail === '% ' || tail === '# ' || tail === '$ ') {
            configured = true;
            emulator.serial0_send(SETUP_CMDS);
            // Save state after commands finish
            setTimeout(async () => {
              try {
                const state = await vmSaveState(emulator);
                await dbPut(db, { state, version: STATE_VERSION });
              } catch(e) { console.error('State save failed:', e); }
            }, 3000);
          }
        }
      });

      term.onData(data => emulator.serial0_send(data));

      const onResize = () => fitAddon.fit();
      window.addEventListener('resize', onResize);
      instRef.current.cleanup = () => {
        window.removeEventListener('resize', onResize);
        if (flushTimeout) clearTimeout(flushTimeout);
        term.dispose();
        emulator.stop();
        instRef.current = null;
      };
    }

    start().catch(err => {
      console.error('Boot failed:', err);
      term.write('\x1b[31mBoot failed: ' + err.message + '\x1b[0m\r\n');
    });

    return () => instRef.current?.cleanup?.();
  }, []);

  return (
    <>
      {loading && <div className="terminal-loading">starting...</div>}
      <div className="terminal-fullscreen" ref={termRef} />
    </>
  );
}
